import { useEffect, useRef, useState } from "react";
import { Terminal, CheckCircle, AlertCircle, Info } from "lucide-react";
import { API_ROUTES } from "../../api/routes";

export function LogStreamViewer({ jobId }) {
    const [logs, setLogs] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [hasError, setHasError] = useState(false);
    const eventSourceRef = useRef(null);
    const logContainerRef = useRef(null);
    const [autoScroll, setAutoScroll] = useState(true);

    useEffect(() => {
        if (!jobId) return;

        const url = API_ROUTES.csvProcessor.logsStream(jobId);
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
            setIsConnected(true);
            setHasError(false);
            console.log("✅ Conectado al stream de logs");
        };

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.logs && data.logs.length > 0) {
                    setLogs((prev) => [...prev, ...data.logs]);
                }

                if (data.complete) {
                    setIsComplete(true);
                    eventSource.close();
                    setIsConnected(false);
                    console.log("✅ Stream completado");
                }
            } catch (e) {
                console.error("Error parsing log event:", e);
            }
        };

        eventSource.onerror = (error) => {
            console.error("❌ Error en stream de logs:", error);
            setIsConnected(false);
            setHasError(true);
            eventSource.close();
        };

        return () => {
            eventSource.close();
            setIsConnected(false);
        };
    }, [jobId]);

    // Auto-scroll al final cuando llegan nuevos logs
    useEffect(() => {
        if (autoScroll && logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [logs, autoScroll]);

    const handleScroll = () => {
        if (!logContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        setAutoScroll(isAtBottom);
    };

    const getLevelIcon = (level) => {
        switch (level) {
            case "error":
                return <AlertCircle size={14} className="text-red-500" />;
            case "warn":
                return <AlertCircle size={14} className="text-yellow-500" />;
            default:
                return <Info size={14} className="text-blue-500" />;
        }
    };

    const getLevelColor = (level) => {
        switch (level) {
            case "error":
                return "text-red-400";
            case "warn":
                return "text-yellow-400";
            default:
                return "text-gray-300";
        }
    };

    const getLevelBadgeColor = (level) => {
        switch (level) {
            case "error":
                return "bg-red-900 text-red-200 border-red-700";
            case "warn":
                return "bg-yellow-900 text-yellow-200 border-yellow-700";
            default:
                return "bg-blue-900 text-blue-200 border-blue-700";
        }
    };

    // Formatear timestamp
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    // ✅ Si hay error de conexión, mostrar mensaje
    if (hasError && logs.length === 0) {
        return (
            <div className="w-full">
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-xl px-5 py-4">
                    <div className="flex items-center gap-3">
                        <Terminal className="text-blue-400" size={22} />
                        <h3 className="text-lg font-semibold text-white">
                            Logs de Procesamiento
                        </h3>
                    </div>
                </div>

                <div className="bg-gray-950 border-x border-b border-gray-700 rounded-b-xl p-8 text-center">
                    <AlertCircle size={48} className="mx-auto mb-4 text-yellow-400" />
                    <p className="text-gray-400 text-lg mb-2">
                        No se pudo conectar al stream de logs
                    </p>
                    <p className="text-gray-600 text-sm">
                        El procesamiento continúa. Revisa la terminal del backend para ver los logs.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-t-xl px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Terminal className="text-blue-400" size={22} />
                    <h3 className="text-lg font-semibold text-white">
                        Logs de Procesamiento
                    </h3>
                    <div className="flex items-center gap-2">
                        <span
                            className={`inline-flex h-2.5 w-2.5 rounded-full ${isConnected
                                ? "bg-green-500 animate-pulse shadow-lg shadow-green-500/50"
                                : isComplete
                                    ? "bg-blue-500"
                                    : "bg-gray-500"
                                }`}
                        />
                        <span className="text-sm text-gray-300 font-medium">
                            {isConnected
                                ? "En Vivo"
                                : isComplete
                                    ? "Completado"
                                    : "Conectando..."}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg">
                        <span className="text-xs text-gray-400">Líneas:</span>
                        <span className="text-sm font-bold text-white">{logs.length}</span>
                    </div>
                    <button
                        onClick={() => setAutoScroll(!autoScroll)}
                        className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${autoScroll
                            ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                            }`}
                    >
                        {autoScroll ? "🔒 Auto-scroll" : "🔓 Manual"}
                    </button>
                </div>
            </div>

            {/* Log Container */}
            <div
                ref={logContainerRef}
                onScroll={handleScroll}
                className="bg-gray-950 text-gray-100 font-mono text-sm p-5 h-[600px] overflow-y-auto border-x border-b border-gray-700 rounded-b-xl"
                style={{ scrollBehavior: autoScroll ? "smooth" : "auto" }}
            >
                {logs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        {isConnected ? (
                            <>
                                <div className="relative mb-4">
                                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
                                    <Terminal className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-500" size={20} />
                                </div>
                                <p className="text-lg font-medium">Esperando logs del pipeline...</p>
                                <p className="text-sm text-gray-600 mt-2">La ejecución ha iniciado</p>
                            </>
                        ) : (
                            <>
                                <Terminal size={48} className="mb-4 text-gray-700" />
                                <p className="text-lg">Conectando al stream de logs...</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="space-y-1">
                        {logs.map((log, index) => (
                            <div
                                key={index}
                                className="group flex gap-3 hover:bg-gray-900/50 px-3 py-2 rounded-lg transition-colors"
                            >
                                {/* Timestamp */}
                                <span className="text-gray-600 text-xs font-medium whitespace-nowrap pt-0.5 group-hover:text-gray-500">
                                    {formatTime(log.timestamp)}
                                </span>

                                {/* Level Badge */}
                                <span
                                    className={`px-2.5 py-0.5 text-xs font-bold rounded border ${getLevelBadgeColor(
                                        log.level
                                    )} whitespace-nowrap`}
                                >
                                    {log.level.toUpperCase()}
                                </span>

                                {/* Message */}
                                <div className={`flex-1 ${getLevelColor(log.level)} leading-relaxed`}>
                                    {log.message}
                                </div>
                            </div>
                        ))}

                        {/* Indicador de stream activo */}
                        {isConnected && (
                            <div className="flex items-center gap-2 px-3 py-2 text-blue-400 text-sm">
                                <div className="animate-pulse">●</div>
                                <span>Recibiendo logs en tiempo real...</span>
                            </div>
                        )}

                        {/* Indicador de completado */}
                        {isComplete && (
                            <div className="flex items-center gap-2 px-3 py-3 mt-4 bg-green-900/20 border border-green-700 rounded-lg">
                                <CheckCircle className="text-green-500" size={18} />
                                <span className="text-green-400 font-semibold">Pipeline completado exitosamente</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer Stats */}
            {logs.length > 0 && (
                <div className="bg-gray-800 border-x border-b border-gray-700 rounded-b-lg px-5 py-2 flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-4">
                        <span>
                            Errores: <strong className="text-red-400">{logs.filter(l => l.level === 'error').length}</strong>
                        </span>
                        <span>
                            Warnings: <strong className="text-yellow-400">{logs.filter(l => l.level === 'warn').length}</strong>
                        </span>
                        <span>
                            Info: <strong className="text-blue-400">{logs.filter(l => l.level === 'info').length}</strong>
                        </span>
                    </div>
                    <span className="text-gray-500">
                        {isComplete ? "Stream finalizado" : isConnected ? "Conectado" : "Desconectado"}
                    </span>
                </div>
            )}
        </div>
    );
}